/**
 * @file image-utils.ts
 * @description Contentstack Image Delivery API URL helpers, plus the adapter that shapes bare CMS
 * assets into the IEnhancedImage contract ImageWrapper consumes.
 */

import { CSLPFieldMapping, IEnhancedImage, IFile } from '@/.generated';

/** Contentstack image hosts (e.g. azure-na-images.contentstack.com) — these serve the Image Delivery API. */
const CONTENTSTACK_IMAGE_HOST = /^[a-z0-9-]*images\.contentstack\.com$/i;

/** Formats WebP conversion must skip: SVG is vector (nothing to re-encode), GIF would risk losing animation. */
const WEBP_EXCLUDED_FORMATS = /\.(svg|gif)$/i;

/** Parses a possibly protocol-relative URL, returning null instead of throwing on malformed input. */
const parseImageUrl = (src: string): URL | null => {
  try {
    return new URL(src.startsWith('//') ? `https:${src}` : src);
  } catch {
    return null;
  }
};

/** Adds Contentstack Image Delivery width/quality resize params; skips SVG/GIF and non-Contentstack hosts, and never overrides an author-supplied value. */
const appendDeliveryParams = (
  src: string | undefined | null,
  { width, quality }: { width?: number; quality?: number }
): string | undefined => {
  if (!src) return undefined;

  const url = parseImageUrl(src);
  if (!url) return src;
  if (!CONTENTSTACK_IMAGE_HOST.test(url.hostname)) return src;
  if (WEBP_EXCLUDED_FORMATS.test(url.pathname)) return src;

  if (!url.searchParams.has('auto') && !url.searchParams.has('format')) {
    url.searchParams.set('auto', 'webp');
  }
  if (width && !url.searchParams.has('width')) {
    url.searchParams.set('width', String(Math.round(width)));
  }
  if (quality && !url.searchParams.has('quality')) {
    url.searchParams.set('quality', String(Math.round(quality)));
  }

  return url.toString();
};

/**
 * Resizes a Contentstack image to `width`/`quality` via the Image Delivery API (plus `auto=webp`),
 * so the CDN — not the Next.js image optimizer — does the resizing/encoding work.
 *
 * Use this for images rendered with `unoptimized`, where Next.js passes the `src` straight to the
 * `<img>` tag without ever calling `/_next/image`.
 */
export function withDeliveryParams(
  src: string,
  params: { width?: number; quality?: number }
): string;
export function withDeliveryParams(
  src: string | undefined | null,
  params: { width?: number; quality?: number }
): string | undefined;
export function withDeliveryParams(
  src?: string | null,
  params: { width?: number; quality?: number } = {}
): string | undefined {
  return appendDeliveryParams(src, params);
}

/** Shaping options for `toEnhancedImage` — everything ImageWrapper reads off an IEnhancedImage. */
interface EnhancedImageOptions {
  /** Accessible name; omit for decorative images and pass `alt=""` to ImageWrapper instead. */
  alternateText?: string;
  /** Intrinsic width to assume when the asset carries no `dimension` (next/image requires one). */
  fallbackWidth?: number;
  /** Intrinsic height to assume when the asset carries no `dimension`. */
  fallbackHeight?: number;
  fit?: IEnhancedImage['image_fit_options'];
  position?: IEnhancedImage['image_position_options'];
  roundedImage?: boolean;
  /** Live Preview mapping for the underlying asset field. */
  cslp?: CSLPFieldMapping;
}

/**
 * Adapts a bare CMS asset — an `IFile`, or any object carrying a `url` — into the `IEnhancedImage`
 * shape `ImageWrapper` consumes, so plain asset fields can render through the same wrapper as
 * `enhanced_image` global fields.
 *
 * Returns `undefined` when there is no URL, which `ImageWrapper` renders as nothing. Always pass
 * `fallbackWidth`/`fallbackHeight` for assets whose `dimension` is not guaranteed — next/image
 * throws without intrinsic sizing unless `fill` is used.
 */
export const toEnhancedImage = (
  file?: Partial<IFile> | null,
  options: EnhancedImageOptions = {}
): IEnhancedImage | undefined => {
  if (!file?.url) return undefined;

  const width = file.dimension?.width ?? options.fallbackWidth;
  const height = file.dimension?.height ?? options.fallbackHeight;

  return {
    image: {
      ...(file as IFile),
      dimension: width && height ? { width, height } : file.dimension,
    },
    alternate_text: options.alternateText,
    image_fit_options: options.fit,
    image_position_options: options.position,
    rounded_image: options.roundedImage ?? false,
    $: options.cslp ? { image: options.cslp } : undefined,
  };
};
